import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  BoxProps,
  Button,
  HStack,
  IconButton,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { RefreshIcon } from '../../../../../components/icons';

type Slot = {
  key: string;
  index: number;
  answer: string;
  caseSensitive?: boolean;
};

interface FillMissingTextProps extends BoxProps {
  text: string;
  slots: Slot[];
  debug?: boolean;
  showCorrect?: boolean;
  submit?: (correct: number, total: number, state: any) => void;
  reset?: () => void;
  initialState?: State;
  isViewOnly?: boolean;
}

type State = {
  answers: Record<string, { value: string; correct: boolean }>;
};

const FillMissingText: React.FC<FillMissingTextProps> = ({
  text,
  slots,
  debug,
  showCorrect,
  submit,
  reset,
  initialState,
  isViewOnly,
  ...props
}) => {
  const { t } = useTranslation();
  const textParts = text.split('xy_____yx');
  const [state, setState] = useState<State>({ answers: {} });
  const [showCorrectAnswers, setShowCorrectAnswers] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [shownCorrect, setShownCorrect] = useState<number>(0);
  const indexMap = useMemo(() => {
    const map: Map<number, Slot> = new Map();

    slots.forEach((s) => {
      map.set(s.index, s);
    });

    return map;
  }, [slots]);

  const setInitialState = () => {
    setState({ answers: {} });
  };

  useEffect(() => {
    if (initialState != null) {
      setState(initialState);
      let correct = 0;

      Object.keys(initialState.answers).forEach((key) => {
        if (initialState.answers[key]!.correct) {
          correct += 1;
        }
      });
      setShowResults(true);
      setShownCorrect(correct);
    } else setInitialState();
  }, [slots, initialState]);

  useEffect(() => {
    console.log({ state });
  }, [state]);

  const _submit = () => {
    setShowResults(true);
    let correct = 0;

    Object.keys(state.answers).forEach((key) => {
      if (state.answers[key]!.correct) {
        correct += 1;
      }
    });

    setShownCorrect(correct);
    if (submit != null) {
      submit(correct, slots.length, state);
    }
  };

  const _reset = () => {
    setInitialState();
    setShowResults(false);
    setShownCorrect(0);
    if (reset != null) reset();
  };

  const ret = (
    <Box {...props}>
      <pre>
        {textParts.map((part, index) => {
          if (indexMap.has(index)) {
            const slot = indexMap.get(index)!;
            return (
              <React.Fragment key={index}>
                {part}
                <Input
                  minW="32px"
                  minH="32px"
                  h="24px"
                  w="auto"
                  htmlSize={state?.answers[slot.key]?.value.length || 10}
                  placeholder={
                    showCorrect || showCorrectAnswers ? slot.answer : undefined
                  }
                  // w="50px"
                  // w="fit-content"
                  // mx={1}
                  display="inline-block"
                  onChange={(e) => {
                    const newState = JSON.parse(JSON.stringify(state));
                    newState.answers[slot.key] = {
                      value: e.target.value,
                      correct: slot.caseSensitive
                        ? e.target.value.trim() === slot.answer
                        : e.target.value.trim().toLocaleLowerCase() ===
                          slot.answer.toLocaleLowerCase(),
                    };
                    setState(newState);
                  }}
                  value={state?.answers[slot.key]?.value}
                  key={slot.key}
                />
              </React.Fragment>
            );
          }
          return <React.Fragment key={index}>{part}</React.Fragment>;
        })}
      </pre>
      <HStack
        minW={showResults ? '300px' : undefined}
        spacing={0}
        gap={2}
        justify="space-between"
      >
        {showResults && (
          <>
            {showResults && (
              <IconButton
                size="sm"
                colorScheme="stemiBlue"
                variant="ghost"
                aria-label="reset"
                icon={<RefreshIcon boxSize="24px" />}
                onClick={_reset}
              >
                {t('fill.reset')}
              </IconButton>
            )}
            <HStack>
              <span>
                {shownCorrect} / {slots.length}
              </span>
            </HStack>
          </>
        )}
        <HStack>
          {!isViewOnly && (
            <Button colorScheme="blue" onClick={_submit}>
              {t('fill.submit')}
            </Button>
          )}
        </HStack>
      </HStack>
    </Box>
  );

  if (debug) {
    return (
      <VStack w="full">
        {ret}
        <Text>
          <pre>{JSON.stringify(state, null, 2)}</pre>
        </Text>
      </VStack>
    );
  }

  return <>{ret}</>;
};

export default FillMissingText;
